import HasID from './HasID';
import HasAddress from './HasAddress';
import TrackedObject from './TrackedObject';
import HasLabel from './HasLabel';
import { User } from './User';
import { TextMessageTemplates } from './TextMessageTemplates';
import { InventoryLocation } from './InventoryLocation';
import { Team } from "@/models/Team";
import { EmailTemplates } from "@/models/EmailTemplates";
import { FaxTemplateOverride } from './Configuration';

export interface Store extends HasID, HasAddress, TrackedObject {
    id: number;
    name: string;
    phoneNumber: string;
    faxNumber: string;
    emailAddress: string;
    webAddress: string;
    npiNumber: string;
    mailNPINumber: string;
    pidNumber: string;
    fedIDNumber: string;
    storeNumber: string;
    storeID: string;
    deaNumber: string;
    ncpdp: string;
    capabilities: StoreCapabilities;
    active: boolean;
    addressCountry: string;
    allowedStates: string[];
    defaultPrinter: string;
    brandingImageId?: number;
    brandingName: string;
    brandingPhone: string;
    alternatePrinterEndpoint: string;
    useAlternatePrinterEndpoint: boolean;
    pharmacistOfRecord: User;
    zplPrinterQueue: string;
    parataQueue: string;
    textMessageTemplates: TextMessageTemplates;
    emailTemplates: EmailTemplates;
    disableReadyForPickupTexts: boolean;
    parLevelAlertRecipients: string[];
    defaultFillingLocation: InventoryLocation;
    launchDate: Date;
    disallowTransferOut: boolean;
    teams: Team[];
    autoRefillNextProcessDateDefaultCadence: number | null;
    autoRefillNextProcessDateDefaultCadenceType: number | null;
    timeZone: StoreTimeZone | null;
    stateOptions: StoreStateOption[];
    cancelRxTaskTeam?: Team;
    faxTemplateOverrides?: Record<string, FaxTemplateOverride>;
    businessHours?: BusinessHours;
}

export interface StoreStateOption {
    state: string;
    pdmp: boolean;
    ship: boolean;
    license?: string;
    secondaryStoreId?: number;
}

export interface DayHours {
    open?: string;
    close?: string;
    isClosed: boolean;
}

export interface BusinessHours {
    monday?: DayHours;
    tuesday?: DayHours;
    wednesday?: DayHours;
    thursday?: DayHours;
    friday?: DayHours;
    saturday?: DayHours;
    sunday?: DayHours;
}

export enum StoreCapabilities {
    TwoStepShipping = 1 << 0,
    UseUSPSInState = 1 << 1,
    HubMode = 1 << 2,
}

export enum StoreTimeZone {
    PacificStandardTime = 0,
    MountainStandardTime = 1,
    USMountainStandardTime = 2,
    CentralStandardTime = 3,
    EasternStandardTime = 4,
    USEasternStandardTime = 5,
}

export class Store extends HasLabel {

    constructor(obj?: Store) {
        super();
        this.teams = [];

        if (obj) {
            Object.assign(this, obj);
            if (obj.teams) {
                this.teams = obj.teams.map(t => new Team(t));
            }

            if (obj.cancelRxTaskTeam) {
                this.cancelRxTaskTeam = new Team(obj.cancelRxTaskTeam);
            }
        }

        if (!this.defaultFillingLocation) {
            this.defaultFillingLocation = new InventoryLocation();
        }

        if (!this.textMessageTemplates) {
            this.textMessageTemplates = new TextMessageTemplates();
        }

        if (!this.emailTemplates) {
            this.emailTemplates = new EmailTemplates();
        }

        if (!this.parLevelAlertRecipients) {
            this.parLevelAlertRecipients = [];
        }

        if (!this.stateOptions) {
            this.stateOptions = [];
        }

        if (!this.faxTemplateOverrides) {
            this.faxTemplateOverrides = {};
        }

        if (!this.addressCountry) {
            this.addressCountry = "United States";
        }
    }

    toString(): string {
        if (this.name) return `${this.id} - ${this.name}`;
        return '<div class="font-weight-light">Store Name</div>';
    }

    get fullAddress(): string {
        return `${this.address1 || ""} ${this.address2 || ""}, ${this.addressCity || ""}, ${this.addressState || ""} ${this.addressZip || ""}`;
    }

    static get flagOptions(): Array<string> {
        return Object.keys(StoreCapabilities)
                     .filter(val => !isNaN(Number(val)))
                     .map(k => StoreCapabilities[Number(k)]);
    }

    get selectedCapabilities(): string[] {
        const ret: string[] = [];
        if (this.capabilities) {
            Store.flagOptions.forEach((o: any) => {
                if (this.capabilities & (StoreCapabilities[o] as any)) ret.push(o);
            });
        }
        return ret;
    }

    set selectedCapabilities(vals: string[]) {
        this.capabilities = 0 << 0;
        vals.forEach((flag: any) => {
            const flagVal: any = StoreCapabilities[flag];
            this.capabilities |= flagVal;
        });
    }

    get selectedCapabilitiesEnums(): Array<StoreCapabilities> {
        return this.selectedCapabilities?.map<StoreCapabilities>((f: any) => (StoreCapabilities[f] as any));
    }
}
